import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/layout/Sidebar';
import Header from '../components/layout/Header';
import { useAuth } from '../hooks';
import { MICA_CASPS, MICA_NCASPS, MICA_EMTS } from '../data/micaData';
import '../styles/MiCARegister.css';

const MiCARegisterNew = () => {
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [activeTab, setActiveTab] = useState('casps');

  // Hardcoded data - transform short keys to full keys
  const [casps] = useState(MICA_CASPS.map(item => ({
    name: item.n,
    country: item.c,
    authority: item.a,
    website: item.w,
    authorisationDate: item.d,
    services: item.s || [],
    passportCountries: item.p,
    lastUpdate: item.u
  })));

  // Extract token name from whitepaper URL
  const extractTokenName = (whitepaper) => {
    if (!whitepaper || whitepaper === 'EMT_NO_WP') return 'N/A';

    // Try to extract token name from URL patterns
    const patterns = [
      /mica-([\w]+)-whitepaper/i,  // mica-eurc-whitepaper, mica-usdc-whitepaper
      /whitepapers?\/([\w]+)\.html/i,  // EUROe.html, eUSD.html
      /whitepaper[_-]([\w]+)\.html/i,  // Whitepaper_EUR.html, Whitepaper_USD.html
      /([\w]+)-WP/i,  // EURCV-WP, EURe-WP
      /([\w]{3,6})-White-Paper/i,  // EUROP-White-Paper
      /whitepaper[_-]([\w]+)-/i,  // whitepaper-eurw-
      /\/([A-Z]{3,6}e?)\.html/i,  // /EUROe.html
    ];

    for (const pattern of patterns) {
      const match = whitepaper.match(pattern);
      if (match && match[1]) {
        return match[1].toUpperCase();
      }
    }

    return 'N/A';
  };

  const [emts] = useState(MICA_EMTS.map(item => ({
    name: item.n,
    country: item.c,
    authority: item.a,
    website: item.w,
    authorisationDate: item.d,
    whitepaper: item.wp,
    tokenName: extractTokenName(item.wp),
    lastUpdate: item.u
  })));

  const [ncasps] = useState(MICA_NCASPS.map(item => ({
    name: item.n,
    country: item.c,
    authority: item.a,
    website: item.w,
    decisionDate: item.d,
    reason: item.r || 'Not specified',
    lastUpdate: item.u
  })));

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedService, setSelectedService] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Sort state
  const [sortColumn, setSortColumn] = useState('authorisationDate');
  const [sortDirection, setSortDirection] = useState('desc');

  // Service label mapping
  const SERVICE_MAP = {
    'a.': 'Custody',
    'b.': 'Operation',
    'c.': 'Exchange',
    'd.': 'Exchange',
    'e.': 'Execution',
    'f.': 'Placement',
    'g.': 'Reception',
    'h.': 'Advice',
    'i.': 'Portfolio',
    'j.': 'Transfer'
  };

  // Service tag colors
  const SERVICE_STYLES = {
    Custody: { bg: 'rgba(59,130,246,0.12)', border: 'rgba(59,130,246,0.3)', text: '#60a5fa' },
    Exchange: { bg: 'rgba(16,185,129,0.12)', border: 'rgba(16,185,129,0.3)', text: '#34d399' },
    Execution: { bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.3)', text: '#fbbf24' },
    Transfer: { bg: 'rgba(139,92,246,0.12)', border: 'rgba(139,92,246,0.3)', text: '#a78bfa' },
    Placement: { bg: 'rgba(239,68,68,0.12)', border: 'rgba(239,68,68,0.3)', text: '#f87171' },
    Reception: { bg: 'rgba(20,184,166,0.12)', border: 'rgba(20,184,166,0.3)', text: '#2dd4bf' },
    Operation: { bg: 'rgba(100,116,139,0.12)', border: 'rgba(100,116,139,0.3)', text: '#94a3b8' },
    Advice: { bg: 'rgba(236,72,153,0.12)', border: 'rgba(236,72,153,0.3)', text: '#f472b6' },
    Portfolio: { bg: 'rgba(249,115,22,0.12)', border: 'rgba(249,115,22,0.3)', text: '#fb923c' }
  };

  // Parse services from service code
  const parseServices = (serviceCode) => {
    if (!serviceCode) return [];
    const services = [];
    const parts = serviceCode.split('|');
    parts.forEach(part => {
      const trimmed = part.trim();
      const prefix = trimmed.substring(0, 2).toLowerCase();
      if (SERVICE_MAP[prefix]) {
        services.push(SERVICE_MAP[prefix]);
      }
    });
    // Deduplicate
    return [...new Set(services)];
  };

  // Parse date DD/MM/YYYY
  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      return new Date(parts[2], parts[1] - 1, parts[0]);
    }
    return null;
  };

  // Relative time
  const getRelativeTime = (dateStr) => {
    const date = parseDate(dateStr);
    if (!date) return '';
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);

    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffWeeks < 4) return `${diffWeeks}wk ago`;
    return `${diffMonths}mo ago`;
  };

  // Get current data based on active tab
  const currentData = activeTab === 'casps' ? casps : activeTab === 'emts' ? emts : ncasps;

  // Filter and sort data
  const filteredData = useMemo(() => {
    let filtered = currentData.filter(item => {
      // Search filter
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const searchable = [
          item.name,
          item.lei,
          item.authority,
          item.website,
          item.country
        ].join(' ').toLowerCase();
        if (!searchable.includes(term)) return false;
      }

      // Country filter
      if (selectedCountry && item.country !== selectedCountry) return false;

      // Service filter (CASP only)
      if (activeTab === 'casps' && selectedService) {
        if (!item.services || !item.services.includes(selectedService)) return false;
      }

      // Date filter
      if (dateFrom || dateTo) {
        const itemDate = parseDate(item.authorisationDate || item.decisionDate);
        if (itemDate) {
          if (dateFrom && itemDate < new Date(dateFrom)) return false;
          if (dateTo && itemDate > new Date(dateTo)) return false;
        }
      }

      return true;
    });

    // Sort
    filtered.sort((a, b) => {
      let aVal = a[sortColumn];
      let bVal = b[sortColumn];

      // Handle dates
      if (sortColumn.includes('Date')) {
        aVal = parseDate(aVal);
        bVal = parseDate(bVal);
      }

      if (aVal === bVal) return 0;
      if (!aVal) return 1;
      if (!bVal) return -1;

      const comparison = aVal > bVal ? 1 : -1;
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [currentData, searchTerm, selectedCountry, selectedService, dateFrom, dateTo, sortColumn, sortDirection, activeTab]);

  // Handle sort
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  // Get unique countries from current tab data
  const countries = useMemo(() => {
    return [...new Set(currentData.map(item => item.country).filter(Boolean))].sort();
  }, [currentData]);

  // Calculate EU countries from CASP data (Phase 1D fix)
  const euCountries = useMemo(() => {
    return new Set(casps.map(c => c.country).filter(Boolean)).size;
  }, [casps]);

  // Get unique services (for CASP tab)
  const services = useMemo(() => {
    if (activeTab !== 'casps') return [];
    const allServices = casps.flatMap(item => item.services || []);
    return [...new Set(allServices)].sort();
  }, [casps, activeTab]);

  // Export CSV
  const exportCSV = () => {
    const headers = activeTab === 'casps'
      ? ['Name', 'LEI', 'Country', 'Authority', 'Services', 'Passport Countries', 'Auth Date', 'Website']
      : activeTab === 'emts'
      ? ['Issuer', 'Token Name', 'Country', 'Authority', 'Auth Date', 'Whitepaper', 'Website']
      : ['Entity Name', 'Country', 'Authority', 'Decision Date', 'Reason', 'Website'];

    const rows = filteredData.map(item => {
      if (activeTab === 'casps') {
        return [
          item.name,
          item.lei,
          item.country,
          item.authority,
          (item.services || []).join('; '),
          item.passportCountries,
          item.authorisationDate,
          item.website
        ];
      } else if (activeTab === 'emts') {
        return [
          item.name,
          item.tokenName,
          item.country,
          item.authority,
          item.authorisationDate,
          item.whitepaper,
          item.website
        ];
      } else {
        return [
          item.name,
          item.country,
          item.authority,
          item.decisionDate,
          item.reason,
          item.website
        ];
      }
    });

    const csvContent = [headers, ...rows].map(row =>
      row.map(cell => `"${(cell || '').toString().replace(/"/g, '""')}"`).join(',')
    ).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mica-${activeTab}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Header
        currentUser={currentUser}
        showUserMenu={showUserMenu}
        setShowUserMenu={setShowUserMenu}
        onLogout={logout}
      />
      <div className="app">
        <Sidebar />
        <main className="main-content">
        <div className="mica-register-page">
          {/* HEADER — identical structure to ComplianceOperatorPage */}
          <div style={{
            padding: '48px 48px',
            borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
            backgroundColor: 'transparent'
          }}>

            {/* Breadcrumb — exactly like Compliance Operator style */}
            <div style={{
              fontSize: '11px',
              fontWeight: 500,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: '#64748b',
              marginBottom: '12px'
            }}>
              COMPLIANCE TERMINAL › MICA REGISTER
            </div>

            {/* H1 — exactly like "AI-Powered Regulatory Intelligence" */}
            <h1 style={{
              fontSize: '32px',
              fontWeight: 600,
              color: '#1e293b',
              letterSpacing: '-0.02em',
              lineHeight: 1.3,
              margin: '0 0 16px 0'
            }}>
              MiCA Register — Complete ESMA Database
            </h1>

            {/* Subtitle — exactly like compliance-operator subtitle */}
            <p style={{
              fontSize: '14px',
              color: '#475569',
              lineHeight: 1.6,
              margin: '0 0 28px 0',
              maxWidth: '700px'
            }}>
              Track {casps.length} authorized CASPs, {emts.length} stablecoin issuers,
              and {ncasps.length} non-compliant entities across the EU.
              Updated weekly from official ESMA data.
            </p>

            {/* Stats row — exactly like "DOCUMENTS INDEXED / AVG RESPONSE / MODEL / REGION" */}
            <div style={{ display: 'flex', gap: '56px', flexWrap: 'wrap', alignItems: 'center' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <div style={{
                  fontSize: '11px',
                  fontWeight: 500,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: '#64748b'
                }}>Authorized CASPs</div>
                <div style={{ fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>
                  {casps.length}
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <div style={{
                  fontSize: '11px',
                  fontWeight: 500,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: '#64748b'
                }}>EU Countries</div>
                <div style={{ fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>
                  {euCountries}
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <div style={{
                  fontSize: '11px',
                  fontWeight: 500,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: '#64748b'
                }}>New This Month</div>
                <div style={{ fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>4</div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <div style={{
                  fontSize: '11px',
                  fontWeight: 500,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: '#64748b'
                }}>Last Updated</div>
                <div style={{ fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>
                  Feb 5, 2026
                </div>
              </div>
            </div>
          </div>

          {/* Content Wrapper */}
          <div className="mica-content-wrapper">
          {/* Tab Navigation */}
          <div className="mica-tabs">
            <button
              className={`mica-tab ${activeTab === 'casps' ? 'active' : ''}`}
              onClick={() => setActiveTab('casps')}
            >
              CASP Register <span className="mica-tab-count">{casps.length}</span>
            </button>
            <button
              className={`mica-tab ${activeTab === 'emts' ? 'active' : ''}`}
              onClick={() => setActiveTab('emts')}
            >
              EMT Stablecoins <span className="mica-tab-count">{emts.length}</span>
            </button>
            <button
              className={`mica-tab ${activeTab === 'ncasps' ? 'active' : ''}`}
              onClick={() => setActiveTab('ncasps')}
            >
              Non-Compliant <span className="mica-tab-count">{ncasps.length}</span>
            </button>
          </div>

          {/* Dark Data Panel - matching ORACLE */}
          <div className="mica-data-panel">
            {/* Panel Header */}
            <div className="mica-panel-header">
              <span className="mica-panel-label">MICA REGISTER</span>
              <span className="mica-panel-status">ESMA VERIFIED</span>
            </div>

          {/* Toolbar */}
          <div className="mica-toolbar">
            <input
              type="text"
              className="mica-search-input"
              placeholder="Search by name, country, LEI, website..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <select
              className="mica-filter-select"
              value={selectedCountry}
              onChange={(e) => setSelectedCountry(e.target.value)}
            >
              <option value="">All Countries</option>
              {countries.map(country => (
                <option key={country} value={country}>{country}</option>
              ))}
            </select>
            {activeTab === 'casps' && (
              <select
                className="mica-filter-select"
                value={selectedService}
                onChange={(e) => setSelectedService(e.target.value)}
              >
                <option value="">All Services</option>
                {services.map(service => (
                  <option key={service} value={service}>{service}</option>
                ))}
              </select>
            )}
            <input
              type="date"
              className="mica-date-input"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
            <input
              type="date"
              className="mica-date-input"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
            <button className="mica-btn-export" onClick={exportCSV}>
              Export CSV
            </button>
          </div>

          {/* Table */}
          <div className="mica-table-container">
              <table className="mica-table">
                <thead>
                  <tr>
                    {activeTab === 'casps' && (
                      <>
                        <th
                          onClick={() => handleSort('name')}
                          className={`sortable ${sortColumn === 'name' ? `sorted-${sortDirection}` : ''}`}
                        >
                          Commercial Name
                        </th>
                        <th
                          onClick={() => handleSort('country')}
                          className={`sortable ${sortColumn === 'country' ? `sorted-${sortDirection}` : ''}`}
                        >
                          Country
                        </th>
                        <th
                          onClick={() => handleSort('authorisationDate')}
                          className={`sortable ${sortColumn === 'authorisationDate' ? `sorted-${sortDirection}` : ''}`}
                        >
                          Authorization Date
                        </th>
                        <th>Services</th>
                        <th>Website(s)</th>
                        <th>Last Updated</th>
                      </>
                    )}
                    {activeTab === 'emts' && (
                      <>
                        <th
                          onClick={() => handleSort('name')}
                          className={`sortable ${sortColumn === 'name' ? `sorted-${sortDirection}` : ''}`}
                        >
                          Issuer Name
                        </th>
                        <th
                          onClick={() => handleSort('country')}
                          className={`sortable ${sortColumn === 'country' ? `sorted-${sortDirection}` : ''}`}
                        >
                          Country
                        </th>
                        <th
                          onClick={() => handleSort('tokenName')}
                          className={`sortable ${sortColumn === 'tokenName' ? `sorted-${sortDirection}` : ''}`}
                        >
                          Token Name
                        </th>
                        <th
                          onClick={() => handleSort('authorisationDate')}
                          className={`sortable ${sortColumn === 'authorisationDate' ? `sorted-${sortDirection}` : ''}`}
                        >
                          Authorization Date
                        </th>
                        <th>Whitepaper</th>
                        <th>Status</th>
                      </>
                    )}
                    {activeTab === 'ncasps' && (
                      <>
                        <th
                          onClick={() => handleSort('name')}
                          className={`sortable ${sortColumn === 'name' ? `sorted-${sortDirection}` : ''}`}
                        >
                          Entity Name
                        </th>
                        <th
                          onClick={() => handleSort('country')}
                          className={`sortable ${sortColumn === 'country' ? `sorted-${sortDirection}` : ''}`}
                        >
                          Country / Authority
                        </th>
                        <th>Website</th>
                        <th
                          onClick={() => handleSort('decisionDate')}
                          className={`sortable ${sortColumn === 'decisionDate' ? `sorted-${sortDirection}` : ''}`}
                        >
                          Decision Date
                        </th>
                        <th>Reason</th>
                        <th>Status</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {filteredData.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="mica-empty">
                        <div className="mica-empty-text">No entities match your filters</div>
                      </td>
                    </tr>
                  ) : (
                    filteredData.map(item => (
                      <tr key={item.id}>
                        {activeTab === 'casps' && (
                          <>
                            <td className="name">
                              {item.website ? (
                                <a href={item.website.startsWith('http') ? item.website : `https://${item.website}`} target="_blank" rel="noopener noreferrer">
                                  {item.name}
                                </a>
                              ) : (
                                item.name
                              )}
                            </td>
                            <td>
                              <span className="mica-country-badge">{item.country}</span>
                            </td>
                            <td className="date">{item.authorisationDate}</td>
                            <td>
                              <div className="mica-service-tags">
                                {item.services && item.services.slice(0, 3).map((service, idx) => (
                                  <span
                                    key={idx}
                                    className={`mica-service-tag ${service.toLowerCase()}`}
                                  >
                                    {service}
                                  </span>
                                ))}
                                {item.services && item.services.length > 3 && (
                                  <span className="mica-service-tag operation">+{item.services.length - 3}</span>
                                )}
                              </div>
                            </td>
                            <td className="website">
                              {item.website && (
                                <a href={item.website.startsWith('http') ? item.website : `https://${item.website}`} target="_blank" rel="noopener noreferrer">
                                  {item.website.replace(/^https?:\/\//, '').substring(0, 24)}
                                  {item.website.length > 24 ? '...' : ''}
                                </a>
                              )}
                            </td>
                            <td className="last-update">{getRelativeTime(item.lastUpdate)}</td>
                          </>
                        )}
                        {activeTab === 'emts' && (
                          <>
                            <td className="name">{item.name}</td>
                            <td>
                              <span className="mica-country-badge">{item.country}</span>
                            </td>
                            <td>{item.tokenName || 'N/A'}</td>
                            <td className="date">{item.authorisationDate}</td>
                            <td className="website">
                              {item.whitepaper && item.whitepaper !== 'EMT_NO_WP' && item.whitepaper.startsWith('http') ? (
                                <a href={item.whitepaper} target="_blank" rel="noopener noreferrer">
                                  View Whitepaper
                                </a>
                              ) : item.whitepaper && item.whitepaper !== 'EMT_NO_WP' ? (
                                <span style={{ color: '#c9d1d9', fontSize: '12px' }}>{item.whitepaper}</span>
                              ) : (
                                <span style={{ color: '#8b949e', fontSize: '12px' }}>No whitepaper</span>
                              )}
                            </td>
                            <td>
                              <span className="mica-status-badge operational">OPERATIONAL</span>
                            </td>
                          </>
                        )}
                        {activeTab === 'ncasps' && (
                          <>
                            <td className="name">{item.name}</td>
                            <td>{item.country} / {item.authority || 'N/A'}</td>
                            <td className="website">
                              {item.website && (
                                <a href={item.website.startsWith('http') ? item.website : `https://${item.website}`} target="_blank" rel="noopener noreferrer">
                                  {item.website.replace(/^https?:\/\//, '').substring(0, 24)}
                                </a>
                              )}
                            </td>
                            <td className="date">{item.decisionDate}</td>
                            <td title={item.reason}>
                              {item.reason && item.reason.length > 60 ? `${item.reason.substring(0, 60)}...` : item.reason}
                            </td>
                            <td>
                              <span className="mica-status-badge non-compliant">NON-COMPLIANT</span>
                            </td>
                          </>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
          {/* End of mica-data-panel */}
          </div>
        </div>
      </main>
      </div>
    </>
  );
};

export default MiCARegisterNew;
